import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import GradingIcon from "@mui/icons-material/Grading";
import GroupsIcon from "@mui/icons-material/Groups";
import IosShareIcon from "@mui/icons-material/IosShare";
import SendIcon from "@mui/icons-material/Send";
import UndoIcon from "@mui/icons-material/Undo";
import WorkIcon from "@mui/icons-material/Work";
import Badge from "@mui/material/Badge";
import Portal from "@mui/material/Portal";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { Masked, MaskedTeam } from "work/_shared/masked-fields";
import {
  ActionsContainer,
  ButtonContainer,
  Content,
  DialogTab,
  Header,
  TabContent,
  TabsContainer,
} from "work/_views/work-dialog-styled-components";
import {
  useApproveProposalMutation,
  useCancelProposalMutation,
  useGetProposalByIdQuery,
  useGiveReviewerApprovalMutation,
  useHireProposalMutation,
  useQuitProposalTeamMutation,
  useRejectProposalMutation,
  useRequestProposalRevisionMutation,
} from "work/proposals/_api/proposal-api";
import { addProposal, getProposalById, updateProposal } from "work/proposals/_store/proposals-redux-slice";
import {
  CommentButtonLabel,
  CommentButtons,
  Fieldset,
  HighlightCommentButton,
  ProposalActionButton,
  ProposalActions,
  ReviewerBadge,
} from "work/proposals/_views/_shared/proposal-dialog-styled-components";
import ConflictsTab from "work/proposals/_views/active/dialog-tabs/active-proposal-conflicts-tab";
import DetailsTab from "work/proposals/_views/active/dialog-tabs/active-proposal-details-tab";
import DiscountTab from "work/proposals/_views/active/dialog-tabs/active-proposal-discount-tab";
import FeeScheduleTab from "work/proposals/_views/active/dialog-tabs/active-proposal-fee-schedule-tab";
import PoliciesTab from "work/proposals/_views/active/dialog-tabs/active-proposal-policies-tab";
import TeamTab from "work/proposals/_views/active/dialog-tabs/active-proposal-team-tab";
import {
  getArePendingComments,
  saveComments,
  updateAutoGeneratedComments,
} from "work/proposals/comments/_store/comments-redux-slice";
import CommentsDrawer from "work/proposals/comments/_view/comments-drawer";
import AutoCommentGenerator from "work/proposals/comments/auto-comments/auto-comment-generator";
import {
  getCommentThreadsByProposal,
  getErrorLoadingCommentThreadsByProposal,
  getIsLoadingCommentThreadsByProposal,
  populateCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import IssueTraverser from "work/proposals/issues/_views/proposal-issue-traverser";
import ProposalIssuesBadge from "work/proposals/issues/_views/proposal-issues-badge";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import ProposalField, {
  ProposalFieldCategory,
  ProposalFieldCategoryType,
  ProposalFieldName,
} from "work/proposals/proposal-fields/proposal-field";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import ReviewerSelector from "work/proposals/reviewers/_views/reviewer-selector";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";

const ChangeNavigationContainer = styled("section")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
  width: "100%",
}));

type ProposalRedlineProps = {
  proposalId: Guid;
  disableEditing?: boolean;
  isSaving: boolean;
  onSaveAndShareChanges: (args?: { proposal?: Proposal; confirmShare?: boolean }) => Promise<string | null | undefined>;
};
export default function ProposalRedlineDialog(props: Readonly<ProposalRedlineProps>) {
  const { proposalId, disableEditing, isSaving, onSaveAndShareChanges } = props;

  const navigate = useNavigate();

  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  const { closeDialog } = useDialog();
  const session = useSession();

  // #region State
  const [activeTab, setActiveTab] = React.useState<ProposalFieldCategoryType>(ProposalFieldCategory.Details);
  const [activeReviewField, setActiveReviewField] = React.useState<ProposalField>();

  const [currentCommentField, setCurrentCommentField] = React.useState<ProposalField | undefined>();
  const [keepCommentsPanelClosed, setKeepCommentsPanelClosed] = React.useState<boolean>(false);
  const [commentsPanelIsOpen, setCommentsPanelIsOpen] = React.useState<boolean>(false);
  const [commentsPanelTitle, setCommentsPanelTitle] = React.useState<string | undefined>();

  const [reviewerSelectorAnchor, setReviewerSelectorAnchor] = React.useState<HTMLButtonElement>();
  const [isManagingReviewers, setIsManagingReviewers] = React.useState<boolean>(false);

  // #region Proposal Queries
  const proposalByIdAPIQuery = useGetProposalByIdQuery(
    { proposalId: proposalId.value, session },
    { skip: !proposalId.value },
  );
  const [requestRevisionAPICommand, requestRevisionResult] = useRequestProposalRevisionMutation();
  const [approveProposalAPICommand, approveProposalResult] = useApproveProposalMutation();
  const [rejectProposalAPICommand, rejectProposalResult] = useRejectProposalMutation();
  const [hireProposalAPICommand, hireProposalResult] = useHireProposalMutation();
  const [cancelProposalAPICommand, cancelProposalResult] = useCancelProposalMutation();
  const [quitProposalTeamAPICommand, quitProposalTeamResult] = useQuitProposalTeamMutation();
  const [giveReviewerApprovalAPICommand, giveReviewerApprovalResult] = useGiveReviewerApprovalMutation();

  const proposalJson = useSelector(getProposalById(proposalId));
  const proposal = useMemo(() => {
    if (!proposalJson || !session.user) return undefined;
    return Proposal.fromJSON({ json: proposalJson, currentUser: session.user, options: { withRedline: true } });
  }, [proposalJson, session.user]);

  const commentThreads = useSelector(getCommentThreadsByProposal(proposal));
  const arePendingComments = useSelector(getArePendingComments(proposal));
  const isLoadingCommentThreads = useSelector(getIsLoadingCommentThreadsByProposal(proposal));
  const wasErrorLoadingCommentThreads = useSelector(getErrorLoadingCommentThreadsByProposal(proposal));
  const isLoadingDialog = proposalByIdAPIQuery.isLoading || isLoadingCommentThreads;

  const isSubmitting =
    requestRevisionResult.isLoading ||
    approveProposalResult.isLoading ||
    rejectProposalResult.isLoading ||
    hireProposalResult.isLoading ||
    cancelProposalResult.isLoading ||
    quitProposalTeamResult.isLoading ||
    giveReviewerApprovalResult.isLoading;

  const isReviewing = useMemo(() => {
    return (
      [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])].some((reviewer) =>
        reviewer.userId.isEqualTo(session.user?.id),
      ) ?? false
    );
  }, [proposal?.clientReviewersCacheKey, proposal?.vendorReviewersCacheKey]);
  const vendorReviewerCount = proposal?.vendorReviewers?.length ?? 0;
  const clientReviewerCount = proposal?.clientReviewers?.length ?? 0;
  const hasReviewers = session.context?.viewingAsVendor ? vendorReviewerCount > 0 : clientReviewerCount > 0;

  const generalFieldUnreadCount =
    commentThreads
      ?.filter((thread) => thread.field.isEqualTo(ProposalField.General))
      .reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0) ?? 0;

  /**
   * Sets the active review field to the first issue/comment in the active tab
   */
  useEffect(() => {
    if (activeReviewField?.category === activeTab) return;
    const proposalFieldCategories = Object.values(ProposalFieldCategory);
    const proposalFieldNames = Object.values(ProposalFieldName);
    const tabIssues: ProposalField[] = _.uniqBy(
      issues?.entries.map((issue) => issue.field).filter((field) => field.category === activeTab) ?? [],
      (field) => field.toJSON(),
    );
    const tabUnreadComments: ProposalField[] = _.uniqBy(
      commentThreads
        ?.filter((thread) => thread.numUnreadMessages && thread.field.category === activeTab)
        .map((thread) => thread.field) ?? [],
      (field) => field.toJSON(),
    );
    const fieldsPendingReview: ProposalField[] = _.chain([...tabUnreadComments, ...tabIssues])
      .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ""}`)
      .sortBy([
        (field) => proposalFieldCategories.indexOf(field.category),
        (field) => proposalFieldNames.indexOf(field.name),
      ])
      .value();
    setActiveReviewField(fieldsPendingReview[0]);
  }, [activeTab]);

  useEffect(() => {
    if (activeReviewField && activeReviewField.category !== activeTab) {
      setActiveTab(activeReviewField?.category ?? activeTab);
    }
  }, [activeReviewField]);

  useEffect(() => {
    if (!session?.canSwitchContext || !proposal) return;
    if (
      session.context?.viewingAsVendor &&
      MaskedTeam.isPresentNotMasked(proposal.clientLeader) &&
      proposal.clientLeader?.userId.isEqualTo(session.user?.id)
    ) {
      (async () => {
        try {
          await session.setAccountViewType(AccountType.Client);
        } catch (error) {
          console.error("Error setting account view type:", error);
          enqueueSnackbar("Failed to switch to client account", { variant: "error" });
        }
      })();
    } else if (
      Masked.isNotMasked(proposal.vendorTeam) &&
      (proposal.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
        proposal.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id)))
    ) {
      (async () => {
        try {
          await session.setAccountViewType(AccountType.Vendor);
        } catch (error) {
          console.error("Error setting account view type:", error);
          enqueueSnackbar("Failed to switch to vendor account", { variant: "error" });
        }
      })();
    }
  }, [proposal]);

  useEffect(() => {
    if (
      !proposal ||
      proposal.supersededById ||
      isLoadingCommentThreads ||
      wasErrorLoadingCommentThreads ||
      commentThreads
    )
      return;
    dispatch(populateCommentThreadsByProposal({ proposal, session }));
  }, [proposal, isLoadingCommentThreads, wasErrorLoadingCommentThreads]);

  useEffect(() => {
    if (!proposal || proposal.supersededById) return;
    generateChangeAutoComments(proposal);
  }, [proposal]);

  function handleTabChange(_event: React.ChangeEvent<object> | null, newTab: ProposalFieldCategoryType) {
    setActiveTab(newTab);
  }

  function handleManageReviewers(event: React.MouseEvent<HTMLButtonElement>) {
    setReviewerSelectorAnchor(event.currentTarget);
    setIsManagingReviewers(true);
  }

  async function handleSubmitRevision() {
    if (!proposal?.redline) throw new Error("No proposal redline to submit.");

    try {
      dispatch(saveComments({ session }));
      let message: string;
      const clientReviewers = proposal?.clientReviewers ?? proposal?.clientReviewers ?? [];
      const vendorReviewers = proposal?.vendorReviewers ?? proposal?.vendorReviewers ?? [];
      if (
        (session.context?.viewingAsVendor &&
          vendorReviewers.length &&
          vendorReviewers.length !== vendorReviewers.filter((reviewer) => reviewer.dateApproved).length) ||
        (!session.context?.viewingAsVendor &&
          clientReviewers.length &&
          clientReviewers.length !== clientReviewers.filter((reviewer) => reviewer.dateApproved).length)
      ) {
        message =
          "Not all reviewers have reviewed the changes. Do you still want to submit them to the other" + " party?";
      } else {
        message = "Doing so will send all your requested changes for review by the other party.";
      }

      const response = await confirm({
        title: "Send Requested Changes?",
        message: message,
        okButtonText: "Send",
      });

      if (response === ConfirmResponse.Cancel) return;

      await requestRevisionAPICommand({ proposal, session }).unwrap();

      enqueueSnackbar("Requested changes sent", { variant: "success" });
      navigate("/proposals/active/");
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`${error}`, { variant: "error" });
    }
  }

  async function handleApproveProposal() {
    dispatch(saveComments({ session }));
    const revision = proposal?.redlinedRevision;
    if (!revision) {
      throw new Error("No redlined revision to approve.");
    }

    if (!proposal?.id) {
      console.error("No proposal to approve");
      return;
    }
    if (!proposal.redline?.isResolved) {
      console.error("Cannot approve proposal where redline is not resolved.");
      return;
    }
    if (!proposal?.isEquivalentTo(revision)) {
      console.error("Cannot approve proposal while there are redlined revision changes.");
    }
    if (!session.user?.id) {
      console.error("No user id to approve proposal");
      return;
    }

    const approveResponse = await confirm({
      title: "Approve Proposal?",
      message: "Doing so will approve the proposal and send it to any other parties for their approval.",
      okButtonText: "Approve",
    });

    if (approveResponse === ConfirmResponse.Cancel) return;

    try {
      await approveProposalAPICommand({ proposalId: proposal.id?.value, session }).unwrap();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to approve proposal", { variant: "error" });
      return;
    }

    if (proposal.userCanHire(session.user)) {
      const hireResponse = await confirm({
        title: "Hire Team?",
        message: "Doing so will approve the proposal and hire the vendor-team.",
        okButtonText: "Hire",
      });

      if (hireResponse === ConfirmResponse.Ok) {
        try {
          await hireProposalAPICommand({ proposalId: proposal.id?.value }).unwrap();
          enqueueSnackbar("Proposal approved and vendor-team hired", { variant: "success" });
        } catch (error) {
          console.error(error);
          enqueueSnackbar("Failed to hire vendor-team", { variant: "error" });
        }
      }
    } else {
      enqueueSnackbar("Proposal approved", { variant: "success" });
    }
  }

  async function handleAccept() {
    if (!proposal?.redline) return;

    const acceptResponse = await confirm({
      title: "Accept All Changes?",
      message: "This will mark all changes as accepted.",
      okButtonText: "Accept",
    });

    if (acceptResponse === ConfirmResponse.Cancel) return;

    let updatedRedline = proposal.redline;
    updatedRedline = updatedRedline.accept();
    handleProposalRedlineChanged(updatedRedline);
  }

  async function handleReject() {
    if (!proposal?.redline) return;
    dispatch(saveComments({ session }));

    const rejectResponse = await confirm({
      title: "Reject All Changes?",
      message: "This will mark all changes as rejected.",
      okButtonText: "Reject",
    });

    if (rejectResponse === ConfirmResponse.Cancel) return;

    let updatedRedline = proposal.redline;
    updatedRedline = updatedRedline.reject();
    handleProposalRedlineChanged(updatedRedline);
  }

  async function handleEndNegotiation() {
    if (!proposal) return;
    dispatch(saveComments({ session }));

    try {
      const endResponse = await confirm({
        title: "End Negotiation?",
        message: "Doing so will end proposal negotiations.",
        okButtonText: "End Negotiation",
      });

      if (endResponse === ConfirmResponse.Cancel) return;

      await rejectProposalAPICommand({ proposalId: proposal.id?.value }).unwrap();
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to reject proposal", { variant: "error" });
    }
  }

  async function handleCancel() {
    if (!proposal?.id) throw new Error("No proposal to cancel.");

    dispatch(saveComments({ session }));

    try {
      const rejectResponse = await confirm({
        title: "Cancel Proposal?",
        message: "Doing so will end proposal negotiations.",
      });

      if (rejectResponse === ConfirmResponse.Cancel) return;

      navigate(`/proposals/active/`);
      await cancelProposalAPICommand({ proposalId: proposal.id?.value }).unwrap();
      enqueueSnackbar("Proposal canceled", { variant: "success" });
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to cancel proposal", { variant: "error" });
    }
  }

  async function handleHire() {
    if (!proposal?.id) throw new Error("No proposal to hire.");

    dispatch(saveComments({ session }));

    try {
      const reviewers = session.context?.viewingAsVendor ? proposal?.vendorReviewers : proposal?.clientReviewers;
      let message: string;
      if (reviewers.length && reviewers.length !== reviewers.map((reviewer) => reviewer.dateApproved).length) {
        message = "Not all reviewers have reviewed the proposal. Do you still want to hire the selected vendor-team?";
      } else {
        message = "Hire the selected vendor-team?";
      }

      const hireResponse = await confirm({
        title: "Hire?",
        message: message,
        okButtonText: "Hire",
      });

      if (hireResponse === ConfirmResponse.Cancel) return;

      await hireProposalAPICommand({ proposalId: proposal.id?.value }).unwrap();

      enqueueSnackbar("Proposal hired", { variant: "success" });
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to hire vendor", { variant: "error" });
    }
  }

  async function handleUndoChanges() {
    if (!proposal?.redline) return;

    const undoResponse = await confirm({
      title: "Undo All?",
      message: "Doing so will revert all changes.",
      okButtonText: "Undo All",
    });

    if (undoResponse === ConfirmResponse.Cancel) return;

    let updatedRedline = proposal.redline;
    updatedRedline = updatedRedline.undo();
    handleProposalRedlineChanged(updatedRedline);
  }

  function shouldDisableTabs(): boolean {
    return isLoadingDialog || isSaving || isManagingReviewers;
  }

  function handleProposalRedlineChanged(newProposalRedline: ProposalRedline) {
    if (proposal?.isArchived) return;
    if (!proposal?.redline) {
      throw new Error("Current proposal redline is missing");
    }
    const updatedProposal = proposal.updateRedline(newProposalRedline);
    dispatch(updateProposal(updatedProposal));
  }

  function generateChangeAutoComments(updatedProposal: Proposal) {
    if (!updatedProposal?.redline) return;
    const autoComments = new AutoCommentGenerator(
      session,
      updatedProposal,
      undefined,
      commentThreads ?? [],
    ).generateAutoCommentsFromRedline();
    dispatch(updateAutoGeneratedComments(autoComments));
  }

  function handleFeeScheduleTemplatesUpdated(templateIds: Guid[], newProposalRedline?: ProposalRedline) {
    if (!proposal) {
      console.error("No proposal to update fee schedule templates");
      return;
    }
    let updatedProposal: Proposal;
    if (session.context?.viewingAsVendor) {
      updatedProposal = proposal.updateVendorFeeScheduleTemplateIds(templateIds);
    } else {
      updatedProposal = proposal.updateClientFeeScheduleTemplateIds(templateIds);
    }
    if (newProposalRedline) {
      updatedProposal = updatedProposal.updateRedline(newProposalRedline);
    }
    dispatch(addProposal(updatedProposal));
  }

  function handleCloseCommentsPanel() {
    setKeepCommentsPanelClosed(true);
    setCommentsPanelIsOpen(false);
    adjustDialogPosition(true);
  }

  async function handleOpenCommentsPanel(field?: ProposalField, title?: string) {
    if (field) setCurrentCommentField(field);
    if (title) setCommentsPanelTitle(title);
    setCommentsPanelIsOpen(true);
    adjustDialogPosition(false);
  }

  const adjustDialogPosition = (panelOpen: boolean) => {
    for (const dialog of document.getElementsByClassName("MuiDialog-root")) {
      dialog.setAttribute("style", `padding-right: ${panelOpen ? "0px" : "400px"}; transition: padding-right 225ms;`);
    }
  };

  async function handleMarkReviewedClicked() {
    if (!proposal?.id) throw new Error("No proposal to mark reviewed");

    try {
      const response = await confirm({
        title: "Mark As Reviewed",
        message: "Are you sure you want to mark this proposal as reviewed?",
        okButtonText: "Mark Reviewed",
      });

      if (response === ConfirmResponse.Cancel) return;

      await giveReviewerApprovalAPICommand({ proposalId: proposal.id.value }).unwrap();
      enqueueSnackbar("Proposal marked as reviewed", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to mark proposal as reviewed", { variant: "error" });
    }
  }

  function hasUserApprovedReview(): boolean {
    const reviewers: ProposalReviewer[] = [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])];
    return reviewers.some((reviewer) => reviewer.userId.isEqualTo(session.user?.id) && reviewer.dateApproved);
  }

  async function handleCommentsClicked(field: ProposalField, name?: string) {
    if (proposal?.id && !keepCommentsPanelClosed) {
      await handleOpenCommentsPanel(field, name);
    } else {
      setCurrentCommentField(field);
      setCommentsPanelTitle(name);
    }
  }

  async function handleTeamMemberQuitProposal(memberId: Guid): Promise<void> {
    if (!proposal?.id || !memberId.isEqualTo(session.user?.id)) {
      throw new Error("No proposal or invalid member to quit proposal team.");
    }

    try {
      await quitProposalTeamAPICommand({ proposalId: proposal.id.value }).unwrap();
      enqueueSnackbar("You have quit the proposal vendor-team", {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to quit proposal vendor-team", { variant: "error" });
    }
  }

  function getShareButtonLabel() {
    const isSubmitting =
      requestRevisionResult.isLoading ||
      approveProposalResult.isLoading ||
      rejectProposalResult.isLoading ||
      hireProposalResult.isLoading ||
      cancelProposalResult.isLoading ||
      quitProposalTeamResult.isLoading ||
      giveReviewerApprovalResult.isLoading;

    if (isSubmitting) return "Submitting...";
    if (isSaving) return "Saving...";
    if (!hasReviewers) return "No Reviewers";
    if (arePendingComments && !proposal?.hasUnsharedRedlineChanges) {
      return "Share Comments";
    }
    if (proposal?.hasUnsharedRedlineChanges) {
      return "Share Changes";
    }
    return "Nothing To Share";
  }

  const issues = proposal?.redline ? ProposalIssues.fromRedline(proposal?.redline) : undefined;
  const manageReviewersDisabled =
    !proposal ||
    !commentThreads ||
    isSaving ||
    isManagingReviewers ||
    reviewerSelectorAnchor !== undefined ||
    !proposal?.id ||
    isReviewing;
  const reviewers = (session.context?.viewingAsVendor ? proposal?.vendorReviewers : proposal?.clientReviewers) ?? [];
  const totalApprovals = reviewers.filter((reviewer) => reviewer.dateApproved).length;
  const totalReviewers = reviewers.length ?? 0;

  return (
    !proposalByIdAPIQuery.isError && (
      <>
        <Header>
          <TabsContainer
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            value={activeTab}
            onChange={handleTabChange}
          >
            <DialogTab
              value={ProposalFieldCategory.Details}
              disabled={shouldDisableTabs()}
              label={
                <ProposalIssuesBadge
                  issues={issues}
                  commentThreads={commentThreads ?? []}
                  category={ProposalFieldCategory.Details}
                />
              }
            />
            <DialogTab
              value={ProposalFieldCategory.Team}
              disabled={shouldDisableTabs()}
              label={
                <ProposalIssuesBadge
                  issues={issues}
                  commentThreads={commentThreads ?? []}
                  category={ProposalFieldCategory.Team}
                />
              }
            />
            <DialogTab
              value={ProposalFieldCategory.FeeSchedule}
              disabled={shouldDisableTabs()}
              label={
                <ProposalIssuesBadge
                  issues={issues}
                  commentThreads={commentThreads ?? []}
                  category={ProposalFieldCategory.FeeSchedule}
                />
              }
            />
            <DialogTab
              value={ProposalFieldCategory.Conflicts}
              disabled={shouldDisableTabs()}
              label={
                <ProposalIssuesBadge
                  issues={issues}
                  commentThreads={commentThreads ?? []}
                  category={ProposalFieldCategory.Conflicts}
                />
              }
            />
            <DialogTab
              value={ProposalFieldCategory.Policies}
              disabled={shouldDisableTabs()}
              label={
                <ProposalIssuesBadge
                  issues={issues}
                  commentThreads={commentThreads ?? []}
                  category={ProposalFieldCategory.Policies}
                />
              }
            />
            <DialogTab
              value={ProposalFieldCategory.Discount}
              disabled={shouldDisableTabs()}
              label={
                <ProposalIssuesBadge
                  issues={issues}
                  commentThreads={commentThreads ?? []}
                  category={ProposalFieldCategory.Discount}
                />
              }
            />
          </TabsContainer>
        </Header>
        <Content>
          <TabContent>
            {(isLoadingDialog || !proposal?.redline || !proposal.clientTeam) && <Loader />}
            {!isLoadingDialog && proposal?.redline && proposal.clientTeam && (
              <>
                {activeTab === ProposalFieldCategory.Details && (
                  <DetailsTab
                    proposalRedline={proposal.redline}
                    issues={issues}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                    activeReviewField={activeReviewField}
                    commentThreads={commentThreads ?? []}
                    onProposalRedlineChange={handleProposalRedlineChanged}
                    onTraverseToNewField={setActiveReviewField}
                    onCommentsClicked={handleCommentsClicked}
                    clientLeaderUserId={
                      Masked.isPresentNotMasked(proposal.clientLeader) ? proposal.clientLeader.userId : null
                    }
                  />
                )}
                {activeTab === ProposalFieldCategory.Team && (
                  <TeamTab
                    proposalRedline={proposal.redline}
                    issues={issues}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                    activeReviewField={activeReviewField}
                    commentThreads={commentThreads ?? []}
                    teamLeader={
                      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) ? proposal.vendorTeam.leader : undefined
                    }
                    onProposalRedlineChange={handleProposalRedlineChanged}
                    onTraverseToNewField={setActiveReviewField}
                    onCommentsClicked={handleCommentsClicked}
                    onTeamMemberQuitProposal={handleTeamMemberQuitProposal}
                  />
                )}
                {activeTab === ProposalFieldCategory.FeeSchedule && (
                  <FeeScheduleTab
                    proposalRedline={proposal.redline}
                    issues={issues}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                    activeReviewField={activeReviewField}
                    commentThreads={commentThreads ?? []}
                    onProposalRedlineChange={handleProposalRedlineChanged}
                    onCommentsClicked={handleCommentsClicked}
                    onFeeScheduleTemplatesUpdated={handleFeeScheduleTemplatesUpdated}
                  />
                )}
                {activeTab === ProposalFieldCategory.Conflicts && (
                  <ConflictsTab
                    proposalRedline={proposal.redline}
                    issues={issues}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                    activeReviewField={activeReviewField}
                    commentsToolTip={"Comments"}
                    commentThreads={commentThreads ?? []}
                    onProposalRedlineChange={handleProposalRedlineChanged}
                    onTraverseToNewField={setActiveReviewField}
                    onCommentsClicked={handleCommentsClicked}
                  />
                )}
                {activeTab === ProposalFieldCategory.Policies && (
                  <PoliciesTab
                    proposalRedline={proposal.redline}
                    issues={issues}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                    activeReviewField={activeReviewField}
                    commentsToolTip={"Comments"}
                    commentThreads={commentThreads ?? []}
                    onProposalRedlineChange={handleProposalRedlineChanged}
                    onTraverseToNewField={setActiveReviewField}
                    onCommentsClicked={handleCommentsClicked}
                  />
                )}
                {activeTab === ProposalFieldCategory.Discount && (
                  <DiscountTab
                    proposalRedline={proposal.redline}
                    issues={issues}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                    activeReviewField={activeReviewField}
                    commentsToolTip={"Comments"}
                    commentThreads={commentThreads ?? []}
                    onProposalRedlineChange={handleProposalRedlineChanged}
                    onTraverseToNewField={setActiveReviewField}
                    onCommentsClicked={handleCommentsClicked}
                  />
                )}
              </>
            )}
          </TabContent>
        </Content>
        <ChangeNavigationContainer>
          <IssueTraverser
            disabled={isSubmitting || isSaving || isManagingReviewers}
            issueFields={_.uniqBy(issues?.entries.map((issue) => issue.field) ?? [], (field) => field.toJSON())}
            unreadCommentFields={_.uniqBy(
              commentThreads?.filter((thread) => thread.numUnreadMessages).map((thread) => thread.field) ?? [],
              (field) => field.toJSON(),
            )}
            currentField={activeReviewField}
            onTraverseToNewField={setActiveReviewField}
          />
        </ChangeNavigationContainer>
        <ActionsContainer>
          <ProposalActions>
            <Fieldset>
              {isReviewing && (
                <Tooltip
                  enterDelay={600}
                  placement="top"
                  title={hasUserApprovedReview() ? "Proposal marked as reviewed" : "Mark proposal as reviewed"}
                >
                  <span>
                    <ProposalActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<GradingIcon />}
                      loading={isSaving}
                      disabled={
                        isLoadingDialog || isSaving || isSubmitting || isManagingReviewers || hasUserApprovedReview()
                      }
                      onClick={handleMarkReviewedClicked}
                    >
                      {hasUserApprovedReview() ? "Reviewed" : "Mark Reviewed"}
                    </ProposalActionButton>
                  </span>
                </Tooltip>
              )}
              {!isReviewing && (
                <>
                  <Tooltip enterDelay={600} placement="top" title={"Add/remove proposal reviewers"}>
                    <ProposalActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<GroupsIcon />}
                      endIcon={
                        <ReviewerBadge
                          overlap="rectangular"
                          invisible={
                            manageReviewersDisabled ||
                            !reviewers.length ||
                            !proposal?.redlinedRevision ||
                            (proposal.redline?.isResolved && proposal.isEquivalentTo(proposal.redlinedRevision))
                          }
                          badgeContent={
                            <Tooltip
                              placement="top"
                              title={
                                totalApprovals === totalReviewers
                                  ? "All reviewers approved"
                                  : "Awaiting reviewer responses"
                              }
                            >
                              <span>
                                <sup>{totalApprovals}</sup>&frasl;<sub>{totalReviewers}</sub>
                              </span>
                            </Tooltip>
                          }
                          color={totalReviewers === totalApprovals ? "success" : "warning"}
                        />
                      }
                      loading={false}
                      disabled={
                        isLoadingDialog ||
                        isSaving ||
                        isManagingReviewers ||
                        reviewerSelectorAnchor !== undefined ||
                        !proposal?.id
                      }
                      onClick={handleManageReviewers}
                    >
                      Reviewers
                    </ProposalActionButton>
                  </Tooltip>
                  {proposal && (
                    <ReviewerSelector
                      proposal={proposal}
                      popoverAnchor={reviewerSelectorAnchor}
                      onPopoverClose={() => {
                        setReviewerSelectorAnchor(undefined);
                        setIsManagingReviewers(false);
                      }}
                      onReviewersChanged={(updatedReviewers) => {
                        if (session.context?.viewingAsVendor) {
                          dispatch(addProposal(proposal?.updateVendorReviewers(updatedReviewers)));
                        } else {
                          dispatch(addProposal(proposal?.updateClientReviewers(updatedReviewers)));
                        }
                      }}
                      onSend={() => onSaveAndShareChanges({ proposal, confirmShare: true })}
                    />
                  )}
                </>
              )}
              <Tooltip
                enterDelay={600}
                placement="top"
                title={
                  proposal?.hasUnsavedRedlineChanges || arePendingComments
                    ? "Share redline changes and/or comments for review"
                    : "No reviewers or no unshared changes/comments"
                }
              >
                <span>
                  <ProposalActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<IosShareIcon />}
                    loading={isSaving}
                    disabled={
                      (!arePendingComments && !proposal?.hasUnsavedRedlineChanges) ||
                      isSaving ||
                      isSubmitting ||
                      isManagingReviewers ||
                      !hasReviewers
                    }
                    onClick={() => proposal && onSaveAndShareChanges({ proposal, confirmShare: true })}
                  >
                    {getShareButtonLabel()}
                  </ProposalActionButton>
                </span>
              </Tooltip>
            </Fieldset>
            {!disableEditing &&
              proposal?.supersedes &&
              !proposal?.userCanHire(session.user) &&
              !proposal.creator?.userId.isEqualTo(session.user?.id) &&
              !proposal.redline?.hasRejections &&
              !proposal.redline?.hasRevisions &&
              !proposal.redline?.isResolved && (
                <>
                  <Tooltip enterDelay={600} placement="top" title="Accept all changes">
                    <span>
                      <ProposalActionButton
                        variant="contained"
                        color="success"
                        startIcon={<DoneAllIcon />}
                        loading={false}
                        disabled={isSubmitting || isLoadingDialog || isSaving || isManagingReviewers}
                        onClick={handleAccept}
                      >
                        Accept All
                      </ProposalActionButton>
                    </span>
                  </Tooltip>
                  <Tooltip enterDelay={600} placement="top" title="Reject all changes">
                    <span>
                      <ProposalActionButton
                        variant="contained"
                        color="error"
                        startIcon={<CloseIcon />}
                        loading={false}
                        disabled={
                          isSubmitting ||
                          isLoadingDialog ||
                          isSaving ||
                          isManagingReviewers ||
                          reviewerSelectorAnchor !== undefined ||
                          !proposal?.id ||
                          proposal.creator?.userId.isEqualTo(session.user?.id) ||
                          !proposal.userCanReject(session.user) ||
                          proposal.redline?.isResolved
                        }
                        onClick={handleReject}
                      >
                        Reject All
                      </ProposalActionButton>
                    </span>
                  </Tooltip>
                </>
              )}
            {!disableEditing && !isReviewing && proposal?.creator?.userId.isEqualTo(session.user?.id) && (
              <Tooltip enterDelay={600} placement="top" title={"Cancel this proposal and end negotiations"}>
                <span>
                  <ProposalActionButton
                    variant="contained"
                    color="error"
                    startIcon={<CloseIcon />}
                    loading={false}
                    disabled={
                      isSubmitting ||
                      isLoadingDialog ||
                      isSaving ||
                      isManagingReviewers ||
                      reviewerSelectorAnchor !== undefined ||
                      !proposal?.id
                    }
                    onClick={handleCancel}
                  >
                    Cancel Proposal
                  </ProposalActionButton>
                </span>
              </Tooltip>
            )}
            {proposal?.userCanApprove(session.user) &&
              proposal?.redline?.isResolved &&
              proposal.redlinedRevision &&
              proposal?.isEquivalentTo(proposal.redlinedRevision) && (
                <Tooltip
                  enterDelay={600}
                  placement="top"
                  title={issues?.entries.length === 0 ? "Submit Approval" : "Resolve Issues to Approve Proposal"}
                >
                  <span>
                    <ProposalActionButton
                      variant="contained"
                      color="success"
                      startIcon={<SendIcon />}
                      loading={isSubmitting}
                      disabled={
                        isSubmitting ||
                        isLoadingDialog ||
                        isSaving ||
                        isManagingReviewers ||
                        !proposal?.redline?.isResolved ||
                        (!proposal?.userCanApprove(session.user) && isReviewing) ||
                        issues?.entries.length !== 0
                      }
                      onClick={handleApproveProposal}
                    >
                      Submit Approval
                    </ProposalActionButton>
                  </span>
                </Tooltip>
              )}

            {!disableEditing && proposal?.userCanRevise(session.user) && (
              <Tooltip
                enterDelay={600}
                placement="top"
                title={issues?.canSubmit ? "Submit Changes" : "Resolve Issues to Submit"}
              >
                <span>
                  <ProposalActionButton
                    variant="contained"
                    color="success"
                    startIcon={<SendIcon />}
                    loading={isSubmitting}
                    disabled={
                      isSubmitting ||
                      isLoadingDialog ||
                      isSaving ||
                      isManagingReviewers ||
                      !proposal.redline?.isResolved ||
                      (issues && !issues.canSubmit) ||
                      !proposal.isModifiedByRedline
                    }
                    onClick={handleSubmitRevision}
                  >
                    Submit Changes
                  </ProposalActionButton>
                </span>
              </Tooltip>
            )}

            {!isReviewing &&
              !disableEditing &&
              proposal?.userCanHire(session.user) &&
              proposal?.redline?.sessionHistory.length === 0 && (
                <Tooltip
                  enterDelay={600}
                  placement="top"
                  title={!proposal?.userCanHire(session.user) && "Awaiting approval by vendors."}
                >
                  <span>
                    <ProposalActionButton
                      variant="contained"
                      color="success"
                      startIcon={<WorkIcon />}
                      loading={false}
                      disabled={
                        isSubmitting ||
                        isLoadingDialog ||
                        isSaving ||
                        isManagingReviewers ||
                        reviewerSelectorAnchor !== undefined ||
                        !proposal?.userCanHire(session.user)
                      }
                      onClick={handleHire}
                    >
                      Hire
                    </ProposalActionButton>
                  </span>
                </Tooltip>
              )}
            {!disableEditing && proposal?.redline?.canBeUndone && (
              <ProposalActionButton
                variant="contained"
                color="secondary"
                startIcon={<UndoIcon />}
                loading={false}
                disabled={isSubmitting || isLoadingDialog || isSaving || isManagingReviewers}
                onClick={handleUndoChanges}
              >
                Undo Changes
              </ProposalActionButton>
            )}
            {!disableEditing && !isReviewing && !proposal?.creator?.userId.isEqualTo(session.user?.id) && (
              <ProposalActionButton
                variant="text"
                color="error"
                startIcon={<BlockIcon />}
                loading={false}
                disabled={
                  isSubmitting ||
                  isLoadingDialog ||
                  isSaving ||
                  isManagingReviewers ||
                  reviewerSelectorAnchor !== undefined ||
                  !proposal?.id
                }
                onClick={handleEndNegotiation}
              >
                End Negotiation
              </ProposalActionButton>
            )}
          </ProposalActions>
          <CommentButtons>
            <ButtonContainer>
              <HighlightCommentButton
                active={activeReviewField?.isEqualTo(ProposalField.General)}
                size="medium"
                color="primary"
                onClick={() => {
                  void handleOpenCommentsPanel(ProposalField.General);
                }}
              >
                <Badge
                  variant={generalFieldUnreadCount ? "standard" : "dot"}
                  color="secondary"
                  overlap="circular"
                  badgeContent={generalFieldUnreadCount > 0 ? generalFieldUnreadCount : undefined}
                  invisible={!commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.General))}
                >
                  <CommentIcon fontSize="medium" />
                </Badge>
              </HighlightCommentButton>
              <CommentButtonLabel variant="button" color="primary">
                Comments
              </CommentButtonLabel>
            </ButtonContainer>
          </CommentButtons>
          {proposal && commentThreads && (
            <Portal>
              <CommentsDrawer
                proposal={proposal}
                commentThreads={commentThreads}
                currentCommentField={currentCommentField}
                activeReviewField={activeReviewField}
                isOpen={commentsPanelIsOpen}
                isSaving={isSaving}
                title={commentsPanelTitle}
                onOpen={() => {
                  void handleOpenCommentsPanel();
                  setKeepCommentsPanelClosed(false);
                }}
                onClose={handleCloseCommentsPanel}
              />
            </Portal>
          )}
        </ActionsContainer>
      </>
    )
  );
}
